'use client';

import type { StripeStoreProduct } from '@inkitt/contracts-common';
import type { CountryCode } from '@inkitt/geo';
import { getFinalPriceAmountAfterCoupon, localizedPriceAmountWithDecimal } from '@inkitt/utils-stripe';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientLocale } from '../../../../../../hooks/useClientLocale';
import { trackClientInfo } from '../../../../../../services/tracking/trackClientInfo';

type PointsContentProps = {
  oneTimePackages: StripeStoreProduct[];
  countryCode: CountryCode;
  storyId?: string;
  episodeNumber?: string;
  coupon?: string;
  onOpenPromoModal: () => void;
  children?: ReactNode;
};

export const PointsContent: React.FC<PointsContentProps> = ({
  oneTimePackages,
  countryCode,
  storyId,
  episodeNumber,
  coupon,
  onOpenPromoModal,
  children,
}) => {
  const { t } = useTranslation();
  const { languageCode } = useClientLocale();

  const handleBundleClick = (bundle: StripeStoreProduct) => {
    trackClientInfo('paywall-bundle-clicked', {
      priceId: bundle.price.id,
      productId: bundle.product.id,
      sku: bundle.product.metadata.sku,
      type: 'points',
    });

    const params = new URLSearchParams();
    params.set('price_id', bundle.price.id);

    if (coupon) {
      params.set('coupon', coupon);
    }

    if (storyId) {
      params.set('storyId', storyId);
    }

    if (episodeNumber) {
      params.set('episodeNumber', episodeNumber);
    }

    const sku = bundle.product.metadata.sku;
    window.location.href = `/u/store/sku/${sku}?${params.toString()}`;
  };

  const formatPrice = (bundle: StripeStoreProduct) => {
    const amount = bundle.price.unit_amount ?? 0;
    const finalAmount = getFinalPriceAmountAfterCoupon({
      amount,
      coupon: bundle.coupon,
      currency: bundle.price.currency,
    });

    return localizedPriceAmountWithDecimal({
      amountInCents: finalAmount,
      currency: bundle.price.currency,
      countryCode,
      languageCode,
    });
  };

  const getPointsFromMetadata = (bundle: StripeStoreProduct): string | null => {
    return bundle.product.metadata.points || bundle.product.metadata.numberOfPoints || null;
  };

  const isRecommended = (bundle: StripeStoreProduct): boolean => {
    const points = getPointsFromMetadata(bundle);
    // Mark 110 points as recommended
    return points === '110' || bundle.product.metadata.recommended === 'true';
  };

  if (oneTimePackages.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-neutral-400">{t('subscription:no_packages_available', 'No packages available')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* Your Balance Slot */}
      {children}

      {/* Points Packages */}
      <div className="flex flex-col gap-5 mb-4">
        {oneTimePackages.map((bundle) => {
          const points = getPointsFromMetadata(bundle);
          const recommended = isRecommended(bundle);

          return (
            <div key={bundle.price.id} className="relative">
              {/* Recommended Badge */}
              {recommended && (
                <div className="absolute -top-2.5 left-4 z-10">
                  <div className="bg-lime-300 text-neutral-900 px-3 py-1.5 rounded-full text-xs font-bold">
                    {t('subscription:recommended', 'Recommended')}
                  </div>
                </div>
              )}

              <button
                onClick={() => handleBundleClick(bundle)}
                className={`w-full rounded-xl p-5 cursor-pointer transition-all flex items-center justify-between border ${
                  recommended
                    ? 'border-lime-400 bg-gradient-to-b from-neutral-400/30 to-transparent'
                    : 'border-neutral-400 bg-gradient-to-b from-neutral-400/20 to-transparent'
                }`}
              >
                <span className="font-bold text-base text-white">
                  {points ? t('points:points_amount', { prefix: '', amount: points }) : bundle.product.name}
                </span>
                <span className="font-bold text-base text-white">{formatPrice(bundle)}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Apply Promo Code Link */}
      <div className="text-center">
        <button
          onClick={onOpenPromoModal}
          className="text-primary-300 hover:text-primary-200 font-bold text-sm underline"
        >
          {t('subscription:apply_promo_code', 'Apply promo code')}
        </button>
      </div>
    </div>
  );
};
